import { useState, useEffect } from 'react';
import { X, Building2, Palette, MessageSquare, Users, CheckCircle2 } from 'lucide-react';
import { sleep } from '../utils/typeSimulation';

// Demo data matching real platform fields
const DEMO = {
  businessName: 'The Bloom Bakery',
  businessType: 'Cafe / Coffee Shop',
  industry: 'Hospitality & Food Service',
  location: 'Manchester, UK',
  website: 'thebloom.co.uk',
  competitors: ['The Coffee Corner', 'Bean & Brew Co.'],
  platforms: [
    { name: 'Instagram', color: 'bg-pink-100 text-pink-700' },
    { name: 'Facebook', color: 'bg-blue-100 text-blue-700' },
  ],
  primaryColor: '#1D4ED8',
  secondaryColor: '#F59E0B',
  fontStyle: 'Friendly & Rounded',
  tones: ['Conversational', 'Inspirational'],
  emojis: 'Occasionally',
  ageRange: '25–45',
  persona: 'Busy professionals who value quality coffee and community experiences',
};

const STEPS = [
  { id: 'business', icon: Building2, label: 'Business Basics' },
  { id: 'brand',    icon: Palette,   label: 'Brand Identity' },
  { id: 'voice',    icon: MessageSquare, label: 'Tone & Voice' },
  { id: 'persona',  icon: Users,     label: 'Ideal Customer' },
];

const BUSINESS_TYPES = [
  'Cafe / Coffee Shop', 'Restaurant / Food Service', 'Retail Store',
  'Creative Studio / Art Gallery', 'Fitness / Wellness / Gym',
  'Professional Services / Consulting', 'Beauty / Salon / Spa',
];

export default function OnboardingPanel({ onClose }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    businessName: '', businessType: '', location: '', website: '',
    competitors: [], platforms: [],
    primaryColor: '', secondaryColor: '', fontStyle: '',
    tones: [], emojis: '',
    ageRange: '', persona: '',
  });
  const [activeField, setActiveField] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  const type = async (text, key) => {
    setActiveField(key);
    for (let i = 1; i <= text.length; i++) {
      setF(p => ({ ...p, [key]: text.substring(0, i) }));
      await sleep(Math.random() * 30 + 25);
    }
    setActiveField(null);
  };

  useEffect(() => {
    const run = async () => {
      // ─── Step 1: Business Basics ─────────────────────────────────────
      await sleep(500);
      await type(DEMO.businessName, 'businessName');
      await sleep(300);
      // Dropdown select
      setActiveField('businessType');
      await sleep(700);
      setF(p => ({ ...p, businessType: DEMO.businessType }));
      setActiveField(null);
      await sleep(300);
      await type(DEMO.location, 'location');
      await sleep(200);
      await type(DEMO.website, 'website');
      await sleep(300);
      // Platforms appear one by one
      setActiveField('platforms');
      for (const pl of DEMO.platforms) {
        await sleep(400);
        setF(p => ({ ...p, platforms: [...p.platforms, pl] }));
      }
      setActiveField(null);
      // Competitors appear
      for (const c of DEMO.competitors) {
        await sleep(400);
        setF(p => ({ ...p, competitors: [...p.competitors, c] }));
      }
      await sleep(700);
      setStep(1);

      // ─── Step 2: Brand Identity ───────────────────────────────────────
      await sleep(400);
      setF(p => ({ ...p, primaryColor: DEMO.primaryColor }));
      await sleep(450);
      setF(p => ({ ...p, secondaryColor: DEMO.secondaryColor }));
      await sleep(400);
      setActiveField('fontStyle');
      await sleep(600);
      setF(p => ({ ...p, fontStyle: DEMO.fontStyle }));
      setActiveField(null);
      await sleep(700);
      setStep(2);

      // ─── Step 3: Tone & Voice ─────────────────────────────────────────
      await sleep(400);
      for (const tone of DEMO.tones) {
        setF(p => ({ ...p, tones: [...p.tones, tone] }));
        await sleep(400);
      }
      await sleep(300);
      setActiveField('emojis');
      await sleep(500);
      setF(p => ({ ...p, emojis: DEMO.emojis }));
      setActiveField(null);
      await sleep(700);
      setStep(3);

      // ─── Step 4: Ideal Customer ───────────────────────────────────────
      await sleep(400);
      setActiveField('ageRange');
      await sleep(500);
      setF(p => ({ ...p, ageRange: DEMO.ageRange }));
      setActiveField(null);
      await sleep(300);
      await type(DEMO.persona, 'persona');
      await sleep(800);
      setIsComplete(true);
    };
    run();
  }, []);

  const pct = isComplete ? 100 : Math.round(((step + 0.5) / STEPS.length) * 100);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-4xl bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="h-20 bg-white" />

        {/* ── Sticky header ── */}
        <div className="sticky top-20 bg-white border-b-2 border-gray-200 p-5 z-20">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-0.5">Business DNA Setup</h2>
              <p className="text-sm text-gray-500">Tell us about your business — AI generates your strategy from this</p>
            </div>
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all opacity-60 hover:opacity-100">
              <X size={24} />
            </button>
          </div>

          {/* Step indicators */}
          <div className="flex items-center">
            {STEPS.map((s, i) => {
              const done = i < step || isComplete;
              const active = i === step && !isComplete;
              return (
                <div key={s.id} className="flex items-center flex-1">
                  <div className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-lg transition-all ${done ? 'text-green-600' : active ? 'text-blue-600' : 'text-gray-400'}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-green-600 text-white' : active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {done ? '✓' : i + 1}
                    </div>
                    <span className="hidden sm:inline truncate">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 rounded transition-all duration-500 ${done ? 'bg-green-300' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-right text-xs text-blue-600 font-semibold mt-1">{pct}% complete</p>
        </div>

        <div className="p-5 space-y-6">

          {/* ── STEP 1: Business Basics ── */}
          <div>
            <SectionHeader index={0} label="Business Basics" done={step > 0 || isComplete} active={step === 0} />
            <div className="grid grid-cols-2 gap-3 mt-3">

              <div className="col-span-2">
                <FieldLabel>Business Name</FieldLabel>
                <SimField active={activeField === 'businessName'} value={f.businessName} typing={activeField === 'businessName'} />
              </div>

              <div>
                <FieldLabel>Business Type</FieldLabel>
                <div className={`px-3 py-2 rounded-lg text-sm border-2 min-h-[38px] transition-all duration-300 ${
                  f.businessType ? 'bg-blue-50 border-blue-300 text-blue-800 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-400'
                }`}>
                  {f.businessType || (activeField === 'businessType'
                    ? <span className="text-gray-400">Selecting...</span>
                    : <span className="text-gray-300">Business type...</span>)}
                  {f.businessType && (
                    <div className="mt-1.5 space-y-0.5">
                      {BUSINESS_TYPES.map((bt, i) => (
                        <div key={i} className={`text-xs px-2 py-0.5 rounded ${bt === f.businessType ? 'bg-blue-600 text-white font-bold' : 'text-blue-600/60'}`}>
                          {bt === f.businessType ? `✓ ${bt}` : bt}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <FieldLabel>Location</FieldLabel>
                <SimField active={activeField === 'location'} value={f.location} typing={activeField === 'location'} />
              </div>

              <div className="col-span-2">
                <FieldLabel>Website</FieldLabel>
                <SimField active={activeField === 'website'} value={f.website} typing={activeField === 'website'} prefix="https://" />
              </div>

              <div>
                <FieldLabel>Preferred Platforms</FieldLabel>
                <div className={`flex flex-wrap gap-1.5 min-h-[38px] px-2 py-1.5 bg-gray-50 border-2 rounded-lg transition-all ${
                  activeField === 'platforms' ? 'border-blue-300' : 'border-gray-200'
                }`}>
                  {f.platforms.map(p => (
                    <span key={p.name} className={`px-2 py-0.5 text-xs font-semibold rounded-full ${p.color} animate-fadeIn`}>{p.name}</span>
                  ))}
                  {activeField === 'platforms' && f.platforms.length === 0 && (
                    <span className="text-xs text-gray-400 py-1">Selecting...</span>
                  )}
                </div>
              </div>

              <div>
                <FieldLabel>Direct Competitors</FieldLabel>
                <div className="flex flex-wrap gap-1.5 min-h-[38px] px-2 py-1.5 bg-gray-50 border-2 border-gray-200 rounded-lg">
                  {f.competitors.map(c => (
                    <span key={c} className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded animate-fadeIn">{c}</span>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {step >= 1 && <Divider />}

          {/* ── STEP 2: Brand Identity ── */}
          {step >= 1 && (
            <div>
              <SectionHeader index={1} label="Brand Identity" done={step > 1 || isComplete} active={step === 1} />
              <div className="grid grid-cols-3 gap-3 mt-3">

                <div>
                  <FieldLabel>Primary Colour</FieldLabel>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-lg min-h-[38px]">
                    {f.primaryColor
                      ? <><div className="w-6 h-6 rounded-full border-2 border-white shadow flex-shrink-0 transition-all duration-500" style={{ background: f.primaryColor }} />
                         <span className="text-xs font-mono text-gray-700">{f.primaryColor}</span></>
                      : <div className="w-6 h-6 rounded-full bg-gray-200 animate-pulse" />}
                  </div>
                </div>

                <div>
                  <FieldLabel>Secondary Colour</FieldLabel>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-lg min-h-[38px]">
                    {f.secondaryColor
                      ? <><div className="w-6 h-6 rounded-full border-2 border-white shadow flex-shrink-0 transition-all duration-500" style={{ background: f.secondaryColor }} />
                         <span className="text-xs font-mono text-gray-700">{f.secondaryColor}</span></>
                      : <div className="w-6 h-6 rounded-full bg-gray-200 animate-pulse" />}
                  </div>
                </div>

                <div>
                  <FieldLabel>Font Style</FieldLabel>
                  <div className={`px-3 py-2 rounded-lg text-sm border-2 min-h-[38px] transition-all duration-300 ${
                    f.fontStyle ? 'bg-purple-50 border-purple-300 text-purple-800 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-400'
                  }`}>
                    {f.fontStyle || (activeField === 'fontStyle' ? 'Selecting...' : '—')}
                  </div>
                </div>

                {/* Font style options */}
                {f.fontStyle && (
                  <div className="col-span-3 flex gap-2">
                    {['Modern & Clean', 'Bold & Strong', 'Elegant & Serif', 'Friendly & Rounded'].map(fs => (
                      <div key={fs} className={`flex-1 text-center text-xs py-1.5 px-2 rounded-lg border-2 font-medium transition-all ${
                        fs === f.fontStyle ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-400'
                      }`}>
                        {fs === f.fontStyle && '✓ '}{fs}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {step >= 2 && <Divider />}

          {/* ── STEP 3: Tone & Voice ── */}
          {step >= 2 && (
            <div>
              <SectionHeader index={2} label="Tone & Voice" done={step > 2 || isComplete} active={step === 2} />
              <div className="grid grid-cols-2 gap-4 mt-3">

                <div>
                  <FieldLabel>Caption Tone (multi-select)</FieldLabel>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {['Professional', 'Conversational', 'Inspiring', 'Educational', 'Promotional'].map(t => (
                      <span key={t} className={`px-2.5 py-1 text-xs font-semibold rounded-full border-2 transition-all duration-300 ${
                        f.tones.includes(t) ? 'bg-green-600 text-white border-green-600 scale-105' : 'bg-gray-50 text-gray-400 border-gray-200'
                      }`}>
                        {f.tones.includes(t) && '✓ '}{t}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <FieldLabel>Use Emojis</FieldLabel>
                  <div className="flex gap-2 mt-1.5">
                    {['Yes', 'No', 'Occasionally'].map(e => (
                      <button key={e} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all duration-300 ${
                        f.emojis === e ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-400 border-gray-200'
                      }`}>
                        {e}
                      </button>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          )}

          {step >= 3 && <Divider />}

          {/* ── STEP 4: Ideal Customer ── */}
          {step >= 3 && (
            <div>
              <SectionHeader index={3} label="Ideal Customer" done={isComplete} active={step === 3} />
              <div className="grid grid-cols-2 gap-3 mt-3">

                <div>
                  <FieldLabel>Age Range</FieldLabel>
                  <div className={`px-3 py-2 rounded-lg text-sm border-2 min-h-[38px] transition-all duration-300 ${
                    f.ageRange ? 'bg-amber-50 border-amber-300 text-amber-800 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-400'
                  }`}>
                    {f.ageRange || (activeField === 'ageRange' ? 'Selecting...' : '—')}
                  </div>
                </div>

                <div className="col-span-2">
                  <FieldLabel>Customer Persona / Lifestyle</FieldLabel>
                  <div className="relative px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-lg text-sm text-gray-900 min-h-[64px] leading-relaxed">
                    {f.persona}
                    {activeField === 'persona' && (
                      <span className="animate-pulse text-blue-600">|</span>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* ── Completion State ── */}
          {isComplete && (
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <CheckCircle2 size={28} className="text-green-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1.5">Business DNA Saved ✓</h3>
              <p className="text-xs text-gray-600 max-w-sm mb-4 leading-relaxed">
                Profile saved to database. AI is generating a personalised 30-day strategy for <strong>The Bloom Bakery</strong> based on your inputs.
              </p>
              <div className="flex gap-3">
                <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all inline-flex items-center gap-2">
                  See AI Strategy Generation
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          )}

        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-400 text-center">Simulated demo — In the real platform, you fill these fields and AI generates your personalised 30-day strategy.</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────

function SectionHeader({ index, label, done, active }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-green-600 text-white' : active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
        {done ? '✓' : index + 1}
      </div>
      <h3 className="text-sm font-bold text-gray-900">{label}</h3>
    </div>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">{children}</label>;
}

function SimField({ value, typing, prefix }) {
  return (
    <div className="relative px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-lg text-sm text-gray-900 min-h-[38px] font-medium">
      {prefix && value && <span className="text-gray-400">{prefix}</span>}
      {value}
      {typing && <span className="animate-pulse text-blue-600 font-thin">|</span>}
    </div>
  );
}

function Divider() {
  return <div className="border-t border-gray-100" />;
}
